import { useEffect, useState, useTransition } from "react";
import { useLocalization } from "../../../hooks/useLocalization";
import { fetchLeaderboardMovers } from "../../../services/leaderboardServices";
import { DailyMoversPayload, DailyPlayerMovers } from "../../../types/leaderboard";
import { formatLocalizedTimestamp } from "../../../utils/leaderboard/format";
import { normalizeRole } from "../../../utils/leaderboard/roles";
import { LEADERBOARD_POLL_MS } from "../../../utils/polling";

export type MoverPeriod = "daily" | "weekly";

const emptyMoversPayload = (): DailyMoversPayload => ({
  period: "daily",
  availableDays: [],
  availableWeeks: [],
  selectedDay: null,
  selectedWeek: null,
  movers: null,
});

const getPeriodOptions = (strings: ReturnType<typeof useLocalization>["strings"]) => [
  { id: "daily" as const, label: strings.LEADERBOARD.PERIOD_DAY },
  { id: "weekly" as const, label: strings.LEADERBOARD.PERIOD_WEEK },
];

const moversMatchSelection = (
  movers: DailyPlayerMovers | null,
  period: MoverPeriod,
  selectedDay: string | null,
  selectedWeek: string | null,
): boolean => {
  if (!movers || movers.period !== period) {
    return false;
  }
  if (period === "daily") {
    return movers.periodKey === selectedDay;
  }
  return movers.periodKey === selectedWeek;
};

export const normalizeMoversPayload = (payload: DailyMoversPayload): DailyMoversPayload => ({
  ...payload,
  movers: payload.movers
    ? {
        ...payload.movers,
        gainers: payload.movers.gainers.map((entry) => ({
          ...entry,
          role: normalizeRole(entry.role),
        })),
        losers: payload.movers.losers.map((entry) => ({
          ...entry,
          role: normalizeRole(entry.role),
        })),
      }
    : null,
});

export const useDailyMovers = () => {
  const { strings, locale } = useLocalization();
  const normalizedInitial = normalizeMoversPayload(emptyMoversPayload());
  const [period, setPeriod] = useState<MoverPeriod>(normalizedInitial.period);
  const [availableDays, setAvailableDays] = useState(normalizedInitial.availableDays);
  const [availableWeeks, setAvailableWeeks] = useState(normalizedInitial.availableWeeks);
  const [selectedDay, setSelectedDay] = useState(normalizedInitial.selectedDay);
  const [selectedWeek, setSelectedWeek] = useState(normalizedInitial.selectedWeek);
  const [movers, setMovers] = useState<DailyPlayerMovers | null>(normalizedInitial.movers);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const teamLogoAlt = (teamName: string) =>
    strings.COMMON.TEAM_LOGO_ALT.replace("{team}", teamName);
  const periodOptions = getPeriodOptions(strings);

  useEffect(() => {
    if (moversMatchSelection(movers, period, selectedDay, selectedWeek)) {
      return;
    }

    startTransition(() => {
      void (async () => {
        setError(null);
        try {
          const payload = await fetchLeaderboardMovers({
            period,
            day: period === "daily" ? selectedDay : null,
            week: period === "weekly" ? selectedWeek : null,
          });
          if (!payload) {
            throw new Error(strings.LEADERBOARD.ERROR_LOAD_MOVERS);
          }
          const normalized = normalizeMoversPayload(payload);
          setAvailableDays(normalized.availableDays);
          setAvailableWeeks(normalized.availableWeeks);
          setSelectedDay(normalized.selectedDay);
          setSelectedWeek(normalized.selectedWeek);
          setMovers(normalized.movers);
        } catch (err) {
          setError(err instanceof Error ? err.message : strings.LEADERBOARD.ERROR_LOAD_MOVERS);
        }
      })();
    });
  }, [movers, period, selectedDay, selectedWeek, strings.LEADERBOARD.ERROR_LOAD_MOVERS]);

  useEffect(() => {
    if (!movers?.live) {
      return;
    }

    const timer = window.setInterval(() => {
      setMovers(null);
    }, LEADERBOARD_POLL_MS);

    return () => {
      window.clearInterval(timer);
    };
  }, [period, movers?.live]);

  const emptyMessage =
    period === "daily"
      ? !availableDays.length
        ? strings.LEADERBOARD.EMPTY_DAILY_WAIT
        : movers
          ? strings.LEADERBOARD.EMPTY_DAILY_NO_CHANGES
          : strings.LEADERBOARD.EMPTY_DAILY_NO_SNAPSHOTS
      : !availableWeeks.length
        ? strings.LEADERBOARD.EMPTY_WEEKLY_WAIT
        : movers
          ? strings.LEADERBOARD.EMPTY_WEEKLY_NO_CHANGES
          : strings.LEADERBOARD.EMPTY_WEEKLY_NO_SNAPSHOTS;

  const gainersTitle =
    period === "daily"
      ? strings.LEADERBOARD.TOP_DAILY_GAINERS
      : strings.LEADERBOARD.TOP_WEEKLY_GAINERS;
  const losersTitle =
    period === "daily"
      ? strings.LEADERBOARD.TOP_DAILY_LOSERS
      : strings.LEADERBOARD.TOP_WEEKLY_LOSERS;

  const liveSubtitle =
    movers?.live && movers.currentAt
      ? (period === "daily"
          ? strings.LEADERBOARD.DAILY_UPDATED_THROUGH
          : strings.LEADERBOARD.WEEKLY_UPDATED_THROUGH
        ).replace("{time}", formatLocalizedTimestamp(movers.currentAt, locale, strings))
      : null;

  const selectPeriod = (next: MoverPeriod) => {
    setPeriod(next);
    setMovers(null);
  };

  const selectDay = (day: string | null) => {
    setSelectedDay(day);
    setMovers(null);
  };

  const selectWeek = (week: string | null) => {
    setSelectedWeek(week);
    setMovers(null);
  };

  return {
    strings,
    period,
    selectPeriod,
    periodOptions,
    availableDays,
    availableWeeks,
    selectedDay,
    selectDay,
    selectedWeek,
    selectWeek,
    movers,
    error,
    isPending,
    teamLogoAlt,
    emptyMessage,
    gainersTitle,
    losersTitle,
    liveSubtitle,
  };
};
